import { useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { X } from "lucide-react";
import { getStockQuote } from "@vaulzo/core/api/market";
import {
  useSaveTransaction,
  useDeleteTransaction,
  useUploadAttachment,
  ledgerErrorMessage,
} from "@vaulzo/core/hooks/usePortfolioMutations";
import { formatUnits, ledgerBrokers } from "@vaulzo/core/domain/stocks";
import { apiErrorDetail } from "@vaulzo/core/http";
import StockSelect from "../StockSelect";
import { Chip, Field, inputClass } from "./ui";
import ProofFiles from "./ProofFiles";

const TYPES = [
  { value: "convert", label: "Convert" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
  { value: "transfer", label: "Transfer" },
];

const NEW_BROKER = "__new__";

const today = () => format(new Date(), "yyyy-MM-dd");
const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
};
const positive = (v) => num(v) > 0;
const str = (v) => (v ? String(v) : "");
const sameBroker = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();

const tickerTrigger =
  "flex w-full min-h-[36px] items-center justify-between gap-2 rounded-lg border border-pf-input bg-white px-[10px] py-2 text-left text-[13px] text-pf-ink outline-none focus:border-pf-focus focus:shadow-[0_0_0_3px_rgba(111,154,130,0.2)]";

function initialDraft(tx, transactions, brokers) {
  const from = tx?.broker || transactions[0]?.broker || brokers[0] || "";
  return {
    type: tx?.type || "convert",
    date: tx?.date || today(),
    broker: from,
    direction: tx?.direction || "to_usd",
    inr: str(tx?.inr_amount),
    usd: tx?.type === "convert" ? str(tx.usd_amount) : "",
    bank: tx?.bank || "",
    symbol: tx?.symbol || "",
    symbolName: "",
    units: str(tx?.units),
    price: str(tx?.price_usd),
    fee: str(tx?.fee_usd),
    cash: tx?.type === "transfer" ? str(tx.usd_amount) : "",
    toBroker: tx?.to_broker || brokers.find((b) => !sameBroker(b, from)) || "",
    items: tx?.items?.map((it) => ({ symbol: it.symbol, units: String(it.units) })) || [],
    note: tx?.note || "",
    tcs: tx?.tcs_inr ?? null, // not editable here; kept as is
  };
}

/**
 * Add or edit one entry, inside the US-stocks modal. The server replays
 * the whole history on save; when that fails its reason shows above the
 * buttons and the draft stays as typed.
 */
export default function EntryModal({ asset, transactions, editing, onClose }) {
  const brokers = useMemo(() => ledgerBrokers(asset, transactions), [asset, transactions]);
  const [d, setD] = useState(() => initialDraft(editing, transactions, brokers));
  const [newBroker, setNewBroker] = useState({ from: false, to: false });
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [queued, setQueued] = useState([]); // proof files for a new entry, uploaded after it saves
  const [attaching, setAttaching] = useState(false);
  const saveTx = useSaveTransaction();
  const deleteTx = useDeleteTransaction();
  const upload = useUploadAttachment();
  const busy = saveTx.isPending || deleteTx.isPending || attaching;

  const set = (patch) => {
    setD((prev) => ({ ...prev, ...patch }));
    setError("");
  };
  const holdingsAt = (b) => (asset.stock_holdings || []).filter((h) => sameBroker(h.broker, b));
  const toOptionsFor = (from) => brokers.filter((b) => !sameBroker(b, from));

  // A broker that isn't known yet (or "New broker…" picked) is typed in.
  const typingFrom = newBroker.from || brokers.length === 0 || (d.broker !== "" && !brokers.includes(d.broker));
  const toOptions = toOptionsFor(d.broker);
  const typingTo = newBroker.to || toOptions.length === 0 || (d.toBroker !== "" && !toOptions.includes(d.toBroker));

  const pickFrom = (value) => {
    if (value === NEW_BROKER) {
      setNewBroker((n) => ({ ...n, from: true }));
      return set({ broker: "" });
    }
    setNewBroker((n) => ({ ...n, from: false }));
    set({ broker: value, toBroker: sameBroker(value, d.toBroker) ? toOptionsFor(value)[0] || "" : d.toBroker });
  };
  const pickTo = (value) => {
    if (value === NEW_BROKER) {
      setNewBroker((n) => ({ ...n, to: true }));
      return set({ toBroker: "" });
    }
    setNewBroker((n) => ({ ...n, to: false }));
    set({ toBroker: value });
  };

  // Buying a ticker pulls its quote (which also rejects non-USD listings)
  // and prefills the price when none was typed.
  const selectStock = async (stock) => {
    set({ symbol: stock.symbol, symbolName: stock.name || "" });
    try {
      const res = await getStockQuote(stock.symbol);
      const p = parseFloat(res.price_usd);
      setD((prev) => ({
        ...prev,
        symbolName: res.name || prev.symbolName,
        price: prev.price || (p > 0 ? String(p) : ""),
      }));
    } catch (err) {
      set({ symbol: "", symbolName: "" });
      setError(apiErrorDetail(err) || `Could not fetch a USD quote for ${stock.symbol}`);
    }
  };

  const rate = positive(d.inr) && positive(d.usd) ? num(d.inr) / num(d.usd) : null;
  const feeOk = d.fee === "" || num(d.fee) >= 0;
  const items = d.items.filter((it) => it.symbol);
  const canSave =
    Boolean(d.date) &&
    d.broker.trim() !== "" &&
    (d.type === "convert"
      ? positive(d.inr) && positive(d.usd)
      : d.type === "transfer"
        ? d.toBroker.trim() !== "" &&
          !sameBroker(d.toBroker, d.broker) &&
          (positive(d.cash) || items.length > 0) &&
          items.every((it) => positive(it.units)) &&
          (d.cash === "" || num(d.cash) >= 0)
        : d.symbol !== "" && positive(d.units) && positive(d.price) && feeOk);

  const payload = () => {
    const base = { type: d.type, date: d.date, broker: d.broker.trim(), note: d.note.trim() };
    if (d.type === "convert") {
      return { ...base, direction: d.direction, inr_amount: num(d.inr), usd_amount: num(d.usd), bank: d.bank.trim(), tcs_inr: d.tcs };
    }
    if (d.type === "transfer") {
      return {
        ...base,
        to_broker: d.toBroker.trim(),
        usd_amount: positive(d.cash) ? num(d.cash) : 0,
        items: items.map((it) => ({ symbol: it.symbol, units: num(it.units) })),
        fee_usd: editing?.type === "transfer" ? editing.fee_usd || 0 : 0, // not editable here; kept as is
      };
    }
    return { ...base, symbol: d.symbol, units: num(d.units), price_usd: num(d.price), fee_usd: positive(d.fee) ? num(d.fee) : 0 };
  };

  const save = async () => {
    let saved;
    try {
      saved = await saveTx.mutateAsync({ assetId: asset.id, id: editing?.id, payload: payload() });
    } catch (err) {
      setError(ledgerErrorMessage(err, "Could not save the entry"));
      return;
    }
    if (!editing && queued.length) {
      setAttaching(true);
      const failed = [];
      for (const q of queued) {
        try {
          await upload.mutateAsync({ assetId: asset.id, txId: saved.transaction.id, file: q.file, kind: q.kind });
        } catch (err) {
          failed.push(`${q.file.name} (${ledgerErrorMessage(err, "upload failed")})`);
        }
      }
      setAttaching(false);
      if (failed.length) toast.error(`Entry saved, but couldn't attach ${failed.join(", ")}`);
    }
    onClose();
  };

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteTx.mutateAsync({ assetId: asset.id, id: editing.id });
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(ledgerErrorMessage(err, "Could not delete the entry"));
    }
  };

  const toUSD = d.direction === "to_usd";

  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(26,34,28,0.35)] p-6 animate-vz-fade"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Edit entry" : "New entry"}
        className="flex max-h-full w-full max-w-[420px] flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_20px_60px_rgba(20,40,28,0.3)] animate-pf-rise-fast"
      >
        <div className="flex flex-none items-center justify-between px-5 pt-4">
          <div className="text-[15px] font-semibold">{editing ? "Edit entry" : "New entry"}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="h-7 w-7 rounded-md text-[18px] leading-none text-pf-muted transition-colors hover:bg-pf-subtle"
          >
            ×
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-5 pb-5 pt-4">
          <div className="flex flex-wrap gap-[6px]">
            {TYPES.map((t) => (
              <Chip key={t.value} active={d.type === t.value} onClick={() => set({ type: t.value })}>
                {t.label}
              </Chip>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <input type="date" max={today()} value={d.date} onChange={(e) => set({ date: e.target.value })} className={inputClass} />
            </Field>
            <Field label={d.type === "transfer" ? "From broker" : "Broker"}>
              <select value={typingFrom ? NEW_BROKER : d.broker} onChange={(e) => pickFrom(e.target.value)} className={inputClass}>
                {brokers.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
                <option value={NEW_BROKER}>New broker…</option>
              </select>
            </Field>
            {typingFrom && (
              <Field label="Broker name" wide>
                <input
                  autoFocus
                  value={d.broker}
                  maxLength={40}
                  onChange={(e) => set({ broker: e.target.value })}
                  placeholder="e.g. Schwab"
                  className={inputClass}
                />
              </Field>
            )}
          </div>

          {d.type === "convert" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={toUSD ? "INR sent" : "INR received"}>
                  <input inputMode="decimal" value={d.inr} onChange={(e) => set({ inr: e.target.value })} className={inputClass} />
                </Field>
                <Field label={toUSD ? "USD received" : "USD sent"}>
                  <input inputMode="decimal" value={d.usd} onChange={(e) => set({ usd: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Rate ₹/$">
                  <input disabled value={rate ? rate.toFixed(2) : ""} placeholder="₹ ÷ $" className={inputClass} />
                </Field>
                <Field label="Bank">
                  <input value={d.bank} maxLength={60} onChange={(e) => set({ bank: e.target.value })} placeholder="e.g. HDFC Bank" className={inputClass} />
                </Field>
              </div>
              <button
                type="button"
                onClick={() => set({ direction: toUSD ? "to_inr" : "to_usd" })}
                className="-mt-1 self-start text-[12px] font-semibold text-pf-accent hover:text-pf-accent-hover"
              >
                {toUSD ? "Converting dollars back to rupees?" : "Converting rupees to dollars?"}
              </button>
            </>
          )}

          {(d.type === "buy" || d.type === "sell") && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ticker" wide>
                {d.type === "buy" ? (
                  <StockSelect
                    value={d.symbol ? `${d.symbol}${d.symbolName ? ` — ${d.symbolName}` : ""}` : ""}
                    onSelect={selectStock}
                    triggerClassName={tickerTrigger}
                  />
                ) : (
                  <HeldTicker holdings={holdingsAt(d.broker)} value={d.symbol} onChange={(s) => set({ symbol: s })} />
                )}
              </Field>
              <Field label="Units">
                <input inputMode="decimal" value={d.units} onChange={(e) => set({ units: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Price $">
                <input inputMode="decimal" value={d.price} onChange={(e) => set({ price: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Fee $">
                <input inputMode="decimal" value={d.fee} onChange={(e) => set({ fee: e.target.value })} placeholder="0" className={inputClass} />
              </Field>
            </div>
          )}

          {d.type === "transfer" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount $">
                  <input inputMode="decimal" value={d.cash} onChange={(e) => set({ cash: e.target.value })} placeholder="0" className={inputClass} />
                </Field>
                <Field label="To broker">
                  <select value={typingTo ? NEW_BROKER : d.toBroker} onChange={(e) => pickTo(e.target.value)} className={inputClass}>
                    {toOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                    <option value={NEW_BROKER}>New broker…</option>
                  </select>
                </Field>
                {typingTo && (
                  <Field label="Destination broker name" wide>
                    <input
                      value={d.toBroker}
                      maxLength={40}
                      onChange={(e) => set({ toBroker: e.target.value })}
                      placeholder="e.g. Schwab"
                      className={inputClass}
                    />
                  </Field>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[12px] font-semibold text-pf-soft">Shares moved</span>
                {d.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_96px_auto] items-center gap-2">
                    <HeldTicker
                      holdings={holdingsAt(d.broker)}
                      value={it.symbol}
                      onChange={(s) => set({ items: d.items.map((x, j) => (j === i ? { ...x, symbol: s } : x)) })}
                    />
                    <input
                      inputMode="decimal"
                      value={it.units}
                      placeholder="Units"
                      onChange={(e) => set({ items: d.items.map((x, j) => (j === i ? { ...x, units: e.target.value } : x)) })}
                      className={inputClass}
                    />
                    <button
                      type="button"
                      aria-label="Remove"
                      onClick={() => set({ items: d.items.filter((_, j) => j !== i) })}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-pf-muted hover:bg-pf-subtle hover:text-pf-neg"
                    >
                      <X className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => set({ items: [...d.items, { symbol: "", units: "" }] })}
                  className="self-start text-[12px] font-semibold text-pf-accent hover:text-pf-accent-hover"
                >
                  + Add shares
                </button>
              </div>
            </>
          )}

          <Field label="Note">
            <textarea
              rows={2}
              maxLength={500}
              value={d.note}
              onChange={(e) => set({ note: e.target.value })}
              className={`${inputClass} resize-y`}
            />
          </Field>

          <ProofFiles assetId={asset.id} entry={editing} entryType={d.type} queued={queued} setQueued={setQueued} />
        </div>

        {error && <div className="flex-none border-t border-pf-line bg-pf-neg-tint px-5 py-[10px] text-[12px] text-pf-neg">{error}</div>}

        <div className="flex flex-none items-center justify-between gap-2 border-t border-pf-line bg-pf-hover px-5 py-3">
          {editing ? (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className={`rounded-lg px-[10px] py-2 text-[13px] font-semibold text-pf-neg transition-colors hover:bg-pf-neg-tint disabled:opacity-45 ${
                confirmDelete ? "bg-pf-neg-tint" : ""
              }`}
            >
              {deleteTx.isPending
                ? "Deleting…"
                : confirmDelete
                  ? editing.attachment_count
                    ? `Delete with ${editing.attachment_count} file${editing.attachment_count === 1 ? "" : "s"}`
                    : "Confirm delete"
                  : "Delete"}
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-pf-input bg-white px-[14px] py-2 text-[13px] font-semibold text-pf-body transition-colors hover:bg-pf-subtle disabled:opacity-45"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave || busy}
              className="rounded-lg bg-pf-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-pf-accent-hover disabled:pointer-events-none disabled:opacity-45"
            >
              {attaching ? "Attaching…" : saveTx.isPending ? "Saving…" : editing ? "Save" : "Add entry"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tickers held at a broker (sells and share transfers can only use those). */
function HeldTicker({ holdings, value, onChange }) {
  const options = holdings.map((h) => h.symbol);
  if (value && !options.includes(value)) options.unshift(value); // an entry for a ticker since sold
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
      <option value="">{options.length ? "Choose a ticker…" : "Nothing held here"}</option>
      {options.map((s) => {
        const h = holdings.find((x) => x.symbol === s);
        return (
          <option key={s} value={s}>
            {s}
            {h ? ` — ${formatUnits(h.units)} held` : ""}
          </option>
        );
      })}
    </select>
  );
}
